import {
  type DynamicClient,
  assertWalletAccountSigningAvailability,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  bufferToBase64,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  switchActiveNetworkInLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Transaction, VersionedTransaction } from '@solana/web3.js';
import type { Wallet as WalletStandardWallet } from '@wallet-standard/base';
import type { SignTransactionInput } from '@wallet-standard/experimental-features';
import bs58 from 'bs58';

import type { SolanaWalletProvider } from '../../../SolanaWalletProvider.types';
import { isVersionedTransaction } from '../../../utils/isVersionedTransaction';
import { solanaExecuteCheckoutTransaction } from '../../../utils/solanaExecuteCheckoutTransaction';
import { solanaTransferAmount } from '../../../utils/solanaTransferAmount';
import { getActiveNetworkCluster } from '../getActiveNetworkCluster';
import { getFeaturesForSolanaStandardWallet } from '../getFeaturesForSolanaStandardWallet';

type CreateWalletProviderFromSolanaStandardWalletParams = {
  dynamicClient: DynamicClient;
  walletStandardWallet: WalletStandardWallet;
};

export const createWalletProviderFromSolanaStandardWallet = ({
  dynamicClient,
  walletStandardWallet,
}: CreateWalletProviderFromSolanaStandardWalletParams): SolanaWalletProvider => {
  const features = getFeaturesForSolanaStandardWallet(walletStandardWallet);

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain: 'SOL',
    displayName: walletStandardWallet.name,
    walletProviderType,
  });

  const connect: SolanaWalletProvider['connect'] = async () => {
    const autoConnectedAccounts = walletStandardWallet.accounts;

    // some wallets like Farcaster will auto inject the account into the wallet object
    // instead of returning it from the connect method
    // so we need to check for that first
    if (
      autoConnectedAccounts?.length &&
      autoConnectedAccounts[0].publicKey &&
      autoConnectedAccounts[0].address
    ) {
      return {
        addresses: [
          {
            address: autoConnectedAccounts[0].address,
            publicKey: autoConnectedAccounts[0].publicKey.toString(),
          },
        ],
      };
    }

    const connectMethod = features['standard:connect']?.connect;

    assertDefined(connectMethod, 'connect method not found in wallet features');

    const result = await connectMethod({ silent: false });

    return {
      addresses: result.accounts?.map((account) => ({
        address: account.address,
        publicKey: account.publicKey?.toString(),
      })),
    };
  };
  const disconnectFeature = features['standard:disconnect']?.disconnect;

  const getActiveNetworkId: SolanaWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: dynamicClient,
        walletProviderKey: key,
      });

  const getCurrentAccount = async () => {
    if (!isConnected() && connect) {
      await connect();
    }

    const account = walletStandardWallet.accounts?.[0];

    assertDefined(account, 'No connected accounts found');

    return account;
  };

  const isConnected = (): boolean => {
    return walletStandardWallet.accounts?.length > 0;
  };

  const getConnectedAddresses: SolanaWalletProvider['getConnectedAddresses'] =
    async () => {
      if (!isConnected() && connect) {
        await connect();
      }

      return {
        addresses: walletStandardWallet.accounts.map(
          (account) => account.address
        ),
      };
    };

  const signAllTransactions: SolanaWalletProvider['signAllTransactions'] =
    async ({ transactions, walletAccount }) => {
      const signTransactionMethod =
        features['solana:signTransaction']?.signTransaction;

      assertDefined(
        signTransactionMethod,
        'signTransaction method not found in wallet features'
      );

      await assertWalletAccountSigningAvailability(
        { walletAccount },
        dynamicClient
      );

      const account = await getCurrentAccount();

      const chain = await getActiveNetworkCluster(
        { walletAccount },
        dynamicClient
      );
      assertDefined(chain, 'Could not get active chain for wallet account');

      const transactionsInput: SignTransactionInput[] = transactions.map(
        (transaction) => ({
          account,
          chain,
          transaction: transaction.serialize({
            requireAllSignatures: false,
          }) as Uint8Array<ArrayBufferLike>,
        })
      );

      const signTransactionResult = await signTransactionMethod(
        ...transactionsInput
      );

      const signedTransactions = signTransactionResult.map(
        ({ signedTransaction }, index) => {
          const inputTransaction = transactions[index];
          type T = typeof inputTransaction;

          if (isVersionedTransaction(inputTransaction)) {
            return VersionedTransaction.deserialize(signedTransaction) as T;
          }
          return Transaction.from(signedTransaction) as T;
        }
      );

      return { signedTransactions };
    };

  const signAndSendTransaction: SolanaWalletProvider['signAndSendTransaction'] =
    async ({ transaction, walletAccount }) => {
      await assertWalletAccountSigningAvailability(
        {
          walletAccount,
        },
        dynamicClient
      );

      const signAndSendTransactionMethod =
        features['solana:signAndSendTransaction']?.signAndSendTransaction;

      assertDefined(
        signAndSendTransactionMethod,
        'signAndSendTransaction method not found in wallet features'
      );

      const account = await getCurrentAccount();

      const chain = await getActiveNetworkCluster(
        { walletAccount },
        dynamicClient
      );
      assertDefined(chain, 'Could not get active chain for wallet account');

      const signedTransactions = await signAndSendTransactionMethod({
        account,
        chain,
        transaction: transaction.serialize({
          requireAllSignatures: false,
        }) as Uint8Array<ArrayBufferLike>,
      });

      const [{ signature }] = signedTransactions;

      return { signature: bs58.encode(signature) };
    };

  const signMessage: SolanaWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    const signMessageMethod = features['solana:signMessage']?.signMessage;

    assertDefined(
      signMessageMethod,
      'signMessage method not found in wallet features'
    );

    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      {
        walletAccount,
      },
      dynamicClient
    );

    const encodedMessage = new TextEncoder().encode(message);

    const account = await getCurrentAccount();

    const messages = await signMessageMethod({
      account,
      message: encodedMessage,
    });

    return { signature: bufferToBase64(messages[0].signature) };
  };

  const signTransaction: SolanaWalletProvider['signTransaction'] = async ({
    transaction,
    walletAccount,
  }) => {
    const signedTransactions = await signAllTransactions({
      transactions: [transaction],
      walletAccount,
    });

    return { signedTransaction: signedTransactions.signedTransactions[0] };
  };

  const switchActiveNetwork: SolanaWalletProvider['switchActiveNetwork'] =
    async ({ networkId }) =>
      switchActiveNetworkInLastKnownRegistry({
        client: dynamicClient,
        networkId,
        walletProviderKey: key,
      });

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter<VoidFunction>({
      removeEventListeners: ({ setupReturnValue }) => {
        assertDefined(setupReturnValue, 'Setup return value not defined');

        setupReturnValue();
      },

      setupEventListeners: ({ handleAccountsChanged }) => {
        const eventsFeature = features['standard:events'];

        return eventsFeature.on('change', ({ accounts }) => {
          handleAccountsChanged({
            addresses: accounts?.map((account) => account.address) ?? [],
          });
        });
      },

      supportedEvents: ['accountsChanged'],
    });

  const terminate: SolanaWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  const walletProvider: SolanaWalletProvider = {
    chain: 'SOL',
    connect,
    disconnect: disconnectFeature,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      solanaExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(walletStandardWallet.name),
    key,
    metadata: {
      displayName: walletStandardWallet.name,
      icon: walletStandardWallet.icon,
    },
    signAllTransactions,
    signAndSendTransaction,
    signMessage,
    signTransaction,
    switchActiveNetwork,
    terminate,
    transferAmount: (args) => solanaTransferAmount(args, dynamicClient),
    walletProviderType,
  };

  return walletProvider;
};
